/**
 * Automatische Store-Bewertungsbitte (Paket A7a).
 *
 * Aufgabenteilung:
 *   - Der SERVER entscheidet, OB ein Konto gefragt werden darf (Konto >= 3 Tage
 *     oder >= 20 eigene Abenteuer-Zuege, 90-Tage-Sperre, atomarer Claim).
 *   - Dieses Modul entscheidet, WANN ein guter Moment ist (reine Regeln,
 *     `evaluateReviewMoment`) und fuehrt die Kette Sperre -> Status -> Claim ->
 *     nativer Aufruf fail-closed aus (`runReviewPrompt`).
 *
 * Bewusst NICHT mehr hier (waren die Ursache fuer die fast ausgebliebenen
 * Anfragen, siehe berichte/A7a.md): geraetelokale 7-Tage-Beobachtung der
 * Installation und drei lokal gezaehlte Nutzungstage. Beide Werte begannen mit
 * jedem neuen Bundle bei null und liessen sich nur auf dem einen Geraet erfuellen.
 *
 * Kein Vorab-Dialog ("Gefaellt dir Lorath?"): Es gibt keine Auswahl, die
 * Zufriedene bevorzugt weiterleitet (Apple 5.6.3 / Google-Richtlinie). Wenn
 * alle Bedingungen stimmen, fragt ausschliesslich der native Dialog des
 * Betriebssystems; ob er erscheint, entscheidet das System (Apple hoechstens 3x
 * je 365 Tage).
 */
export const REVIEW_PROTOCOL_VERSION = 2;
/** Lokale 90-Tage-Sperre. Eigener Schluessel: ein alter Bundle-Stand kann den neuen Zustand nicht missverstehen. */
export const REVIEW_PROMPT_LOCK_KEY = 'lorath_review_lock_v3';
/** Vorgaengerschluessel (nur lesend, damit eine bereits laufende Sperre erhalten bleibt). */
export const REVIEW_PROMPT_LEGACY_LOCK_KEY = 'lorath_review_safe_v2';

const DAY = 86_400_000;
const COOLDOWN = 90 * DAY;

/** Nicht in den ersten 2 Minuten einer Sitzung. */
export const REVIEW_MIN_SESSION_MS = 2 * 60_000;
/** Eine neue Sitzung beginnt, wenn die App mindestens so lange im Hintergrund war. */
export const REVIEW_NEW_SESSION_AFTER_MS = 30 * 60_000;
/** Leseruhe nach einer KI-Antwort, bevor gefragt werden darf. */
export const REVIEW_REPLY_SETTLE_MS = 6_000;
/** Ruhe nach dem Verlassen eines Gespraechs, bevor gefragt werden darf. */
export const REVIEW_EXIT_SETTLE_MS = 2_500;
/** Nach Dialog, Fehler, Kaufdialog (Overlay) oder Hintergrund so lange nicht fragen. */
export const REVIEW_DISTURBANCE_QUIET_MS = 45_000;
/** Nach dem Besuch einer Kauf-/Mitgliedschaftsseite (oder deren Verlassen) deutlich laenger: nie direkt nach einem Kauf. */
export const REVIEW_PURCHASE_QUIET_MS = 10 * 60_000;
/** Nach der letzten Taste (und bei vorhandenem Entwurf gar nicht) fragen. */
export const REVIEW_TYPING_QUIET_MS = 8_000;
/** Ein guter Moment wartet hoechstens so lange auf Ruhe, danach verfaellt er (keine Warteschlange). */
export const REVIEW_ARM_WINDOW_MS = 60_000;
/** Taktung der Ruhe-Pruefung, solange ein Moment wartet. */
export const REVIEW_POLL_MS = 1_000;
/** Nach einer Server-Absage (nicht berechtigt/gesperrt) mindestens so lange nicht erneut fragen. */
export const REVIEW_SERVER_RECHECK_MS = 30 * 60_000;
/** Nach einem technischen Fehler mindestens so lange nicht erneut versuchen. */
export const REVIEW_ERROR_RECHECK_MS = 5 * 60_000;

type StorageLike = Pick<Storage, 'getItem' | 'setItem'>;
interface Lock { cooldownUntil: number }
export interface ReviewPromptStatus { protocol?: number; shouldPrompt?: boolean; messageCount?: number }

// ─── Zeitpunkt-Regeln (rein, ohne DOM) ─────────────────────────────────────────

export type ReviewTrigger = 'reply' | 'exit';
export type ReviewMomentVerdict =
  | 'ok'
  | 'no_reply'
  | 'pending_send'
  | 'session_young'
  | 'after_purchase'
  | 'expired'
  | 'settling'
  | 'disturbed';

export interface ReviewMomentInput {
  trigger: ReviewTrigger;
  now: number;
  /** Beginn der aktuellen Sitzung (App-Start, Anmeldung oder Rueckkehr nach langer Pause). */
  sessionStartedAt: number;
  /** Ende der letzten fehlerfreien KI-Antwort in diesem Gespraech; null = keine. */
  lastReplyAt: number | null;
  /** Zeitpunkt, an dem der Moment bereitgestellt wurde (Antwort-Ende bzw. Verlassen des Gespraechs). */
  armedAt: number;
  /** Letzte Aktivitaet, nach der Ruhe herrschen muss (Antwort, Audio-Ende, Taste, Seitenwechsel). */
  settledSince: number;
  /** Letzter harter Stoerer: Dialog, Fehler, Kaufdialog, Hintergrund. */
  lastDisturbanceAt: number | null;
  /** Letzter Besuch einer Kauf-/Mitgliedschaftsseite (bzw. deren Verlassen); fehlt = nie. */
  lastPurchaseAt?: number | null;
  /** Noch laufende eigene Zuege im Gespraech. */
  pendingSends: number;
}

/** Warten lohnt sich nur bei diesen Urteilen; alle anderen sind endgueltig. */
export function isReviewMomentWaiting(verdict: ReviewMomentVerdict): boolean {
  return verdict === 'settling' || verdict === 'disturbed';
}

export function evaluateReviewMoment(input: ReviewMomentInput): ReviewMomentVerdict {
  if (input.lastReplyAt === null) return 'no_reply';
  if (input.now - input.armedAt > REVIEW_ARM_WINDOW_MS) return 'expired';
  if (input.pendingSends > 0) return 'pending_send';
  if (input.now - input.sessionStartedAt < REVIEW_MIN_SESSION_MS) return 'session_young';
  // Endgueltig, nicht wartend: 10 Minuten passen nicht in das 60-s-Fenster; der naechste gute Moment zaehlt.
  if (input.lastPurchaseAt != null && input.now - input.lastPurchaseAt < REVIEW_PURCHASE_QUIET_MS) return 'after_purchase';
  if (input.lastDisturbanceAt !== null && input.now - input.lastDisturbanceAt < REVIEW_DISTURBANCE_QUIET_MS) return 'disturbed';
  const settle = input.trigger === 'reply' ? REVIEW_REPLY_SETTLE_MS : REVIEW_EXIT_SETTLE_MS;
  if (input.now - input.settledSince < settle) return 'settling';
  return 'ok';
}

// ─── Lokale Sperre (Verteidigung in der Tiefe; der Server bleibt massgeblich) ──

function writeLock(storage: StorageLike, lock: Lock): void {
  const raw = JSON.stringify(lock);
  storage.setItem(REVIEW_PROMPT_LOCK_KEY, raw);
  if (storage.getItem(REVIEW_PROMPT_LOCK_KEY) !== raw) throw new Error('Review persistence failed');
}

function parseCooldown(raw: string | null): number | null {
  if (!raw) return null;
  try {
    const value = (JSON.parse(raw) as { cooldownUntil?: unknown }).cooldownUntil;
    return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null;
  } catch { return null; }
}

/** Liest die Sperre; fehlt sie, wird sie angelegt und die Speicherung dabei nachgewiesen. */
function readLock(storage: StorageLike): Lock {
  const current = parseCooldown(storage.getItem(REVIEW_PROMPT_LOCK_KEY));
  if (current !== null) return { cooldownUntil: current };
  const lock: Lock = { cooldownUntil: parseCooldown(storage.getItem(REVIEW_PROMPT_LEGACY_LOCK_KEY)) ?? 0 };
  writeLock(storage, lock);
  return lock;
}

// ─── Ausfuehrung ───────────────────────────────────────────────────────────────

export type ReviewPromptOutcome =
  | 'skipped_not_native'
  | 'skipped_busy'
  | 'skipped_unsafe'
  | 'skipped_lock'
  | 'skipped_server'
  | 'skipped_error'
  | 'prompted_native';

export interface RunReviewPromptDeps {
  isNative: boolean;
  storage: StorageLike | null;
  /** Wird nach JEDEM asynchronen Schritt erneut gefragt; false bricht fail-closed ab. */
  isSafe: () => boolean;
  fetchStatus: () => Promise<ReviewPromptStatus | null>;
  claim: () => Promise<{ protocol?: number; claimed?: boolean }>;
  requestReview: () => Promise<void>;
  now?: () => number;
}

let running = false;

function restoreLock(storage: StorageLike, lock: Lock): void {
  try { writeLock(storage, lock); } catch { /* Der Server-Claim bleibt die massgebliche Sperre. */ }
}

/**
 * Sperre -> Status -> (lokale Sperre schreiben) -> atomarer Server-Claim -> nativer Aufruf.
 * Die lokale Sperre steht VOR dem Claim, damit ein Speicherfehler nie zu einer
 * unbegrenzten Wiederholung fuehrt; sie wird zurueckgenommen, wenn der Server
 * nicht reserviert hat (Absage oder Netzfehler) — sonst wuerde ein einzelner
 * Netzfehler 90 Tage Ruhe kosten, obwohl nichts gefragt wurde.
 */
export async function runReviewPrompt(deps: RunReviewPromptDeps): Promise<ReviewPromptOutcome> {
  if (!deps.isNative) return 'skipped_not_native';
  if (running) return 'skipped_busy';
  running = true;
  try {
    if (!deps.storage || !deps.isSafe()) return 'skipped_unsafe';
    const now = deps.now?.() ?? Date.now();
    const previous = readLock(deps.storage);
    if (previous.cooldownUntil > now) return 'skipped_lock';
    const status = await deps.fetchStatus();
    if (!deps.isSafe()) return 'skipped_unsafe';
    if (status?.protocol !== REVIEW_PROTOCOL_VERSION || status.shouldPrompt !== true) return 'skipped_server';
    writeLock(deps.storage, { cooldownUntil: now + COOLDOWN });
    if (!deps.isSafe()) { restoreLock(deps.storage, previous); return 'skipped_unsafe'; }
    let claim: { protocol?: number; claimed?: boolean };
    try {
      claim = await deps.claim();
    } catch (error) {
      restoreLock(deps.storage, previous);
      throw error;
    }
    if (claim.protocol !== REVIEW_PROTOCOL_VERSION || claim.claimed !== true) {
      restoreLock(deps.storage, previous);
      return 'skipped_server';
    }
    // Ab hier ist das Konto serverseitig gesperrt: auch ein spaeter unsicherer Zustand gibt nichts frei.
    if (!deps.isSafe()) return 'skipped_unsafe';
    await deps.requestReview();
    return 'prompted_native';
  } catch { return 'skipped_error'; }
  finally { running = false; }
}
